import { AttachmentsEntity, UpdateAttachmentsDto } from '@crm/dto';
import { ApiResponseInterface, ApiResponseStatus, AttachmentsStatus } from '@crm/interface';
import { API_RESPONSE, prepareUpdateFields } from '@crm/utils';
import { Injectable, Logger } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { Repository } from 'typeorm';
import { Response } from 'express';
import { createReadStream, existsSync, mkdirSync, unlinkSync, writeFileSync } from 'fs';
import { join, extname } from 'path';
import { randomUUID } from 'crypto';

const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const MAX_FILE_SIZE = parseInt(process.env.MAX_FILE_SIZE_BYTES || '10485760'); // 10MB default

@Injectable()
export class AttachmentsService {
  private readonly logger = new Logger(AttachmentsService.name);
  private readonly uploadDir: string;
  private readonly baseUrl: string;

  constructor(
    @InjectRepository(AttachmentsEntity)
    private readonly attachmentRepo: Repository<AttachmentsEntity>,
  ) {
    this.uploadDir =
      process.env.FILE_UPLOAD_PATH ||
      join(process.cwd(), 'file_uploads', 'acc-crm');
    this.baseUrl =
      process.env.FILE_UPLOAD_BASE_URL ||
      'http://localhost:3000/api/attachments/file';

    if (!existsSync(this.uploadDir)) {
      mkdirSync(this.uploadDir, { recursive: true });
    }
  }

  // ====== UPLOAD FILE ======
  async upload(file: Express.Multer.File, bucketName: string): Promise<ApiResponseInterface> {
    try {
      if (!ALLOWED_MIME_TYPES.includes(file.mimetype)) {
        return {
          status: ApiResponseStatus.ERROR,
          code: 400,
          message: `File type '${file.mimetype}' is not allowed.`,
          data: null,
        };
      }

      if (file.size > MAX_FILE_SIZE) {
        return {
          status: ApiResponseStatus.ERROR,
          code: 400,
          message: `File size exceeds the ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`,
          data: null,
        };
      }

      const fileId = randomUUID();
      const ext = extname(file.originalname);
      const storedFileName = `${fileId}${ext}`;
      const storagePath = join(this.uploadDir, storedFileName);

      writeFileSync(storagePath, file.buffer);

      const publicUrl = `${this.baseUrl}/${fileId}`;

      const newAttachment = this.attachmentRepo.create({
        bucket_name: bucketName,
        storage_path: storagePath,
        public_url: publicUrl,
        file_type: file.mimetype,
        file_size: file.size,
        file_name: file.originalname,
        status: AttachmentsStatus.ACTIVE,
      });

      await this.attachmentRepo.save(newAttachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.CREATE.MESSAGE,
        data: newAttachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.CREATE.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.CREATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== SERVE FILE ======
  async serveFile(id: string, res: Response): Promise<void> {
    try {
      const attachment = await this.attachmentRepo.findOne({ where: { id } });

      if (!attachment || attachment.status === AttachmentsStatus.DELETED) {
        res.status(404).json({
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.MESSAGE,
          data: null,
        });
        return;
      }

      if (!existsSync(attachment.storage_path)) {
        res.status(404).json({
          status: ApiResponseStatus.ERROR,
          code: 404,
          message: 'File not found on disk.',
          data: null,
        });
        return;
      }

      res.setHeader('Content-Type', attachment.file_type);
      res.setHeader(
        'Content-Disposition',
        `inline; filename="${attachment.file_name}"`,
      );

      const stream = createReadStream(attachment.storage_path);
      stream.pipe(res);
    } catch (error) {
      this.logger.error(error.message, error.stack);
      res.status(500).json({
        status: ApiResponseStatus.ERROR,
        code: 500,
        message: 'Failed to serve file.',
        data: null,
      });
    }
  }

  // ====== GET ALL ATTACHMENTS ======
  async getAll(): Promise<ApiResponseInterface> {
    try {
      const attachments = await this.attachmentRepo.find();
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.GET_ALL.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.GET_ALL.MESSAGE,
        data: attachments,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.GET_ALL.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.GET_ALL.MESSAGE,
        data: error,
      };
    }
  }

  // ====== GET ATTACHMENT BY ID ======
  async getById(id: string): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.attachmentRepo.findOne({ where: { id } });
      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.CODE,
          message: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.MESSAGE,
          data: null,
        };
      }
      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.GET_BY_ID.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.GET_BY_ID.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.GET_BY_ID.MESSAGE,
        data: error,
      };
    }
  }

  // ====== UPDATE ATTACHMENT ======
  async update(dto: UpdateAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.attachmentRepo.findOne({ where: { id: dto.id } });
      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.ATTACHMENT.UPDATE.CODE,
          message: API_RESPONSE.ERROR.ATTACHMENT.UPDATE.MESSAGE,
          data: null,
        };
      }

      const updateData = prepareUpdateFields(dto);
      Object.assign(attachment, updateData);
      await this.attachmentRepo.save(attachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.UPDATE.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.UPDATE.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.UPDATE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== SOFT DELETE ATTACHMENT ======
  async softDelete(dto: UpdateAttachmentsDto): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.attachmentRepo.findOne({ where: { id: dto.id } });
      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.ATTACHMENT.DELETE.CODE,
          message: API_RESPONSE.ERROR.ATTACHMENT.DELETE.MESSAGE,
          data: null,
        };
      }

      attachment.status = AttachmentsStatus.DELETED;
      await this.attachmentRepo.save(attachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.DELETE.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.DELETE.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.DELETE.MESSAGE,
        data: error,
      };
    }
  }

  // ====== DELETE FILE FROM DISK ======
  async deleteFile(id: string): Promise<ApiResponseInterface> {
    try {
      const attachment = await this.attachmentRepo.findOne({ where: { id } });
      if (!attachment) {
        return {
          status: ApiResponseStatus.ERROR,
          code: API_RESPONSE.ERROR.ATTACHMENT.DELETE.CODE,
          message: API_RESPONSE.ERROR.ATTACHMENT.DELETE.MESSAGE,
          data: null,
        };
      }

      if (existsSync(attachment.storage_path)) {
        unlinkSync(attachment.storage_path);
      }

      attachment.status = AttachmentsStatus.DELETED;
      await this.attachmentRepo.save(attachment);

      return {
        status: ApiResponseStatus.SUCCESS,
        code: API_RESPONSE.SUCCESS.ATTACHMENT.DELETE.CODE,
        message: API_RESPONSE.SUCCESS.ATTACHMENT.DELETE.MESSAGE,
        data: attachment,
      };
    } catch (error) {
      this.logger.error(error.message, error.stack);
      return {
        status: ApiResponseStatus.ERROR,
        code: API_RESPONSE.ERROR.ATTACHMENT.DELETE.CODE,
        message: API_RESPONSE.ERROR.ATTACHMENT.DELETE.MESSAGE,
        data: error,
      };
    }
  }
}
